import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { documentV3Schema, pageV3Schema, type PageV3 } from '@docgen/schema';
import { getJson, getList, sendJson, type PaginationMeta } from '@/lib/api';
import type { ProposalRecord } from './api';

/**
 * The proposal page library's data client, on `/api/v3/proposals/library`
 * (same `generate_proposals` grant as the rest of proposals). Every call and
 * every response shape lives here, so a change to the API contract is a
 * change to this one file. Responses are parsed with Zod at this boundary and
 * trusted inward.
 */

/** Who can see a library item. */
export const PAGE_LIBRARY_VISIBILITY = {
  private: 'private',
  team: 'team',
} as const;
export type PageLibraryVisibility = (typeof PAGE_LIBRARY_VISIBILITY)[keyof typeof PAGE_LIBRARY_VISIBILITY];

/** Which items a list asks for: the caller's own, the team's shared ones, or both. */
export const PAGE_LIBRARY_SCOPE = {
  mine: 'mine',
  team: 'team',
  all: 'all',
} as const;
export type PageLibraryScope = (typeof PAGE_LIBRARY_SCOPE)[keyof typeof PAGE_LIBRARY_SCOPE];

/** The most pages one library item holds (the API enforces the same limit). */
export const PAGE_LIBRARY_MAX_PAGES = 10;
/** The longest name and description the API accepts. */
export const PAGE_LIBRARY_NAME_MAX = 120;
export const PAGE_LIBRARY_DESCRIPTION_MAX = 500;

/** A page as `@docgen/schema` defines it (that package is on Zod 3, so it is checked through its own parser). */
const pageSchema = z.custom<PageV3>((value) => pageV3Schema.safeParse(value).success, 'Not a proposal page');

/** An item as the API sends it; `owner` is flattened on the way in. */
const libraryItemWireSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  description: z.string().nullish().transform((value) => value ?? ''),
  visibility: z.enum([PAGE_LIBRARY_VISIBILITY.private, PAGE_LIBRARY_VISIBILITY.team]),
  owner: z.object({ id: z.string(), name: z.string() }),
  /** The caller saved it. */
  isMine: z.boolean().default(false),
  /** The caller may rename, reshare or delete it: the owner, or an admin. The API decides. */
  canEdit: z.boolean().default(false),
  pageCount: z.number().int().min(0),
  usageCount: z.number().int().min(0).default(0),
  lastUsedAt: z.string().nullish().transform((value) => value ?? null),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  /** The first page, for a thumbnail without loading the whole item. */
  firstPage: pageSchema.nullish().transform((value) => value ?? null),
  /** The opening of the pages' text, or the text around the search words. */
  snippet: z.string().default(''),
});

/** `owner: { id, name }` → `ownerId` and `ownerName`, the shape the screens read. */
function flattenOwner<T extends { readonly owner: { readonly id: string; readonly name: string } }>(
  raw: T,
): Omit<T, 'owner'> & { readonly ownerId: string; readonly ownerName: string } {
  const { owner, ...rest } = raw;
  return { ...rest, ownerId: owner.id, ownerName: owner.name };
}

const libraryItemSummarySchema = libraryItemWireSchema.transform(flattenOwner);
/** One library item as a list shows it. */
export type LibraryItemSummary = z.infer<typeof libraryItemSummarySchema>;

const libraryItemSchema = libraryItemWireSchema
  .extend({
    pages: z.array(pageSchema).min(1),
    dataFragment: z.record(z.string(), z.unknown()).default({}),
  })
  .transform(flattenOwner);
/** One library item with its pages and the author data they bind to. */
export type LibraryItem = z.infer<typeof libraryItemSchema>;

/** The proposal a library insert saved: an id and a valid v3 document, at least. */
const proposalRecordSchema = z.custom<ProposalRecord>((value) => {
  if (typeof value !== 'object' || value === null) return false;
  const record: Record<string, unknown> = { ...value };
  return typeof record['id'] === 'string' && documentV3Schema.safeParse(record['document']).success;
}, 'Not a proposal');

const insertResultSchema = z.object({
  proposal: proposalRecordSchema,
  /** The new pages' ids, in order. */
  insertedPageIds: z.array(z.string()).default([]),
  /** Where the first new page landed (the API clamps the requested index). */
  atIndex: z.number().int().min(0).optional(),
  /** Author data keys the proposal lacked, now added. */
  mergedKeys: z.array(z.string()).default([]),
  /** Author data keys the proposal already had, so the item's values were not copied in. */
  skippedKeys: z.array(z.string()).default([]),
});
/** What adding a library item to a proposal returns. */
export type LibraryInsertResult = z.infer<typeof insertResultSchema>;

/** What saving pages to the library sends. The server reads the pages from the saved proposal. */
export interface SaveLibraryItemInput {
  readonly proposalId: string;
  readonly pageIds: readonly string[];
  readonly name: string;
  readonly description?: string;
  readonly visibility: PageLibraryVisibility;
}

/** What a person fills in when saving pages; the caller adds the proposal and page ids. */
export interface LibraryDraft {
  readonly name: string;
  readonly description: string;
  readonly visibility: PageLibraryVisibility;
}

/** The item a library insert adds: its id, and its name for the notice afterwards. */
export interface LibraryInsertTarget {
  readonly id: string;
  readonly name: string;
}

/**
 * What the open proposal editor offers the page library (its Pages panel, and
 * BESTie's cards through the editor bridge). Every action saves pending edits
 * first, because the server reads and writes the stored proposal.
 */
export interface EditorLibraryActions {
  /** Saves these pages (stored in document order) as a library item. */
  readonly save: (pageIds: readonly string[], draft: LibraryDraft) => Promise<LibraryItemSummary>;
  /** Drafts a name and description for these pages. */
  readonly suggest: (pageIds: readonly string[]) => Promise<LibrarySuggestion>;
  /**
   * Adds an item's pages at `atIndex` (after the selected page, or at the end,
   * when absent) and swaps in the document the server saved.
   */
  readonly insert: (item: LibraryInsertTarget, atIndex?: number) => Promise<void>;
}

/** A rename, description or sharing change. */
export interface UpdateLibraryItemInput {
  readonly name?: string;
  readonly description?: string;
  readonly visibility?: PageLibraryVisibility;
}

/** The columns the API can order a list by. */
export const PAGE_LIBRARY_SORT_FIELD = {
  name: 'name',
  updatedAt: 'updatedAt',
  lastUsedAt: 'lastUsedAt',
  usageCount: 'usageCount',
} as const;
export type PageLibrarySortField = (typeof PAGE_LIBRARY_SORT_FIELD)[keyof typeof PAGE_LIBRARY_SORT_FIELD];

export interface ListLibraryItemsInput {
  readonly scope: PageLibraryScope;
  /** Search text over name, description and the pages' words. */
  readonly q?: string;
  /** Server-side order; without it the API returns newest first (or by relevance with `q`). */
  readonly sort?: { readonly field: PageLibrarySortField; readonly direction: 'asc' | 'desc' };
  /** 1-based page of results. */
  readonly page?: number;
}

/** One page of a library list. */
export interface LibraryItemPage {
  readonly items: LibraryItemSummary[];
  readonly pagination: PaginationMeta;
}

/** A drafted name and description for some pages, from the same drafter BESTie uses. */
const suggestionSchema = z.object({
  name: z.string(),
  description: z.string().default(''),
  pageCount: z.number().int().min(0),
});
export type LibrarySuggestion = z.infer<typeof suggestionSchema>;

const LIBRARY_PATH = '/proposals/library';
/** The most items one list request returns (the API's cap). */
export const PAGE_LIBRARY_LIST_LIMIT = 50;

/** Library items the caller can see, in the given scope, one page at a time. */
export async function listLibraryItems(input: ListLibraryItemsInput, signal?: AbortSignal): Promise<LibraryItemPage> {
  const q = input.q?.trim() ?? '';
  const { items, pagination } = await getList<unknown>(
    LIBRARY_PATH,
    {
      scope: input.scope,
      limit: PAGE_LIBRARY_LIST_LIMIT,
      ...(q ? { q } : {}),
      ...(input.sort ? { sort: input.sort.field, dir: input.sort.direction } : {}),
      ...(input.page && input.page > 1 ? { page: input.page } : {}),
    },
    signal,
  );
  return { items: z.array(libraryItemSummarySchema).parse(items), pagination };
}

/**
 * Drafts a name and description from the pages' own words (never values
 * bound to the deal). Saves nothing; the server reads the stored proposal,
 * so save pending edits first.
 */
export async function suggestLibraryDetails(input: {
  readonly proposalId: string;
  readonly pageIds: readonly string[];
}): Promise<LibrarySuggestion> {
  return suggestionSchema.parse(await sendJson<unknown>('post', `${LIBRARY_PATH}/suggest`, input));
}

/** One library item with its pages. */
export async function getLibraryItem(id: string, signal?: AbortSignal): Promise<LibraryItem> {
  return libraryItemSchema.parse(await getJson<unknown>(`${LIBRARY_PATH}/${id}`, undefined, signal));
}

/** Saves pages of a (saved) proposal as a new library item. */
export async function saveLibraryItem(input: SaveLibraryItemInput): Promise<LibraryItemSummary> {
  return libraryItemSummarySchema.parse(await sendJson<unknown>('post', LIBRARY_PATH, input));
}

/** Renames, describes, shares or makes private an item (owner or admin only). */
export async function updateLibraryItem(id: string, patch: UpdateLibraryItemInput): Promise<LibraryItemSummary> {
  return libraryItemSummarySchema.parse(await sendJson<unknown>('patch', `${LIBRARY_PATH}/${id}`, patch));
}

/** Saves the caller's own private copy of an item they can see. */
export async function copyLibraryItem(id: string): Promise<LibraryItemSummary> {
  return libraryItemSummarySchema.parse(await sendJson<unknown>('post', `${LIBRARY_PATH}/${id}/copy`));
}

/** Deletes an item (owner or admin only). Proposals that already used it keep their pages. */
export async function deleteLibraryItem(id: string): Promise<void> {
  await sendJson<unknown>('delete', `${LIBRARY_PATH}/${id}`);
}

/**
 * Adds an item's pages to a proposal, after the page at `atIndex - 1` (or at
 * the end). The server copies the pages with new ids, merges the item's author
 * data where the proposal has none, and saves through the normal commit.
 */
export async function insertLibraryItem(
  proposalId: string,
  input: { readonly itemId: string; readonly atIndex?: number },
): Promise<LibraryInsertResult> {
  return insertResultSchema.parse(await sendJson<unknown>('post', `/proposals/${proposalId}/library-insert`, input));
}

/** Query keys, named once so a save or delete can invalidate every list. */
export const PAGE_LIBRARY_QUERY_KEY = ['proposal-library'] as const;
const LIBRARY_LIST_KEY = 'list';
const LIBRARY_ITEM_KEY = 'item';

/** Items stay fresh for a minute: they change only when someone saves or shares. */
const LIBRARY_STALE_MS = 60_000;

/**
 * Library items in one scope, optionally searched.
 *
 * @param input - Scope and search text.
 * @param enabled - False while the list is not on screen.
 */
export function useLibraryItems(input: ListLibraryItemsInput, enabled = true) {
  const q = input.q?.trim() ?? '';
  const page = input.page ?? 1;
  return useQuery({
    queryKey: [...PAGE_LIBRARY_QUERY_KEY, LIBRARY_LIST_KEY, input.scope, q, input.sort?.field, input.sort?.direction, page],
    queryFn: ({ signal }) =>
      listLibraryItems({ scope: input.scope, q, page, ...(input.sort ? { sort: input.sort } : {}) }, signal),
    placeholderData: keepPreviousData,
    enabled,
    staleTime: LIBRARY_STALE_MS,
  });
}

/**
 * One item with its pages, for a thumbnail or a preview. Cached per id, so a
 * card and its preview share one request.
 *
 * @param id - The item id.
 */
export function useLibraryItem(id: string) {
  return useQuery({
    queryKey: [...PAGE_LIBRARY_QUERY_KEY, LIBRARY_ITEM_KEY, id],
    queryFn: ({ signal }) => getLibraryItem(id, signal),
    staleTime: LIBRARY_STALE_MS,
  });
}
